import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';

import { TFunction } from 'i18next';

interface ChartData {
  date: string[];
  count_approved_mice: number[];
  count_approved_not_mice: number[];
  count_empty?: number[];
}

export const simpleLineChart = (
  chartData: ChartData,
  t: TFunction,
  isBarChart: boolean = false,
  isRat: boolean = false,
) => {
  const type = isBarChart ? 'bar' : 'area';

  const combinedData = (chartData.date || []).map((dateStr, index) => {
    return {
      timestamp: dayjs(dateStr).valueOf(),
      mice: chartData.count_approved_mice?.[index] || 0,
      notMice: chartData.count_approved_not_mice?.[index] || 0,
      empty: chartData.count_empty?.[index] || 0,
    };
  });

  combinedData.sort((a, b) => a.timestamp - b.timestamp);

  const sortedDates = combinedData.map(item => item.timestamp);
  const sortedMice = combinedData.map(item => item.mice);
  const sortedNotMice = combinedData.map(item => item.notMice);
  const sortedEmpty = combinedData.map(item => item.empty);

  const chartOptions: ApexOptions = {
    series: [
      {
        name: isRat ? t('MCTDetail.charts.simple.val_1_rat') : t('MCTDetail.charts.simple.val_1'),
        type: type,
        data: sortedMice,
      },
      {
        name: isRat ? t('MCTDetail.charts.simple.val_2_rat') : t('MCTDetail.charts.simple.val_2'),
        type: type,
        data: sortedNotMice,
        color: '#0ab39c',
      },
      {
        name: t('MCTDetail.charts.simple.val_3'),
        type: type,
        data: sortedEmpty,
        color: '#ffe529',
      },
    ],
    chart: {
      height: 350,
      type: type,
      zoom: { enabled: true },
      toolbar: { show: true },
    },
    grid: {
      padding: { bottom: 20 },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
    },
    legend: {
      offsetY: 0,
      position: 'bottom',
    },
    markers: {
      size: isBarChart ? 0 : 3,
      shape: 'circle',
    },
    stroke: {
      width: isBarChart ? 0 : 3,
      curve: ['smooth', 'smooth', 'smooth'],
    },
    fill: {
      opacity: isBarChart ? 1 : 0.6,
      type: isBarChart ? 'solid' : 'gradient',
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        columnWidth: '50%',
        barHeight: '80%',
        dataLabels: {
          hideOverflowingLabels: false,
          position: 'top',
          total: { enabled: false },
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: isBarChart ? -20 : 0,
      style: {
        fontSize: '12px',
        colors: isBarChart ? ['#333'] : undefined,
      },
      formatter: (val: number) => {
        if (isBarChart) {
          return val === 0 ? '' : val.toString();
        } else {
          return val.toString();
        }
      },
    },
    xaxis: {
      title: {
        text: t('MCTDetail.charts.simple.x_axis'),
        offsetY: 12,
        style: { fontSize: '12px' },
      },
      type: 'category',
      categories: sortedDates,
      labels: {
        formatter: (val: any) => {
          if (!val) return '';
          return dayjs(val).format('DD-MM-YYYY');
        },
        rotate: -90,
        rotateAlways: true,
        hideOverlappingLabels: false,
        offsetY: 10,
      },
    },
    yaxis: {
      title: {
        text: isRat ? t('MCTDetail.charts.simple.y_axis_2') : t('MCTDetail.charts.simple.y_axis'),
        style: { fontSize: '12px' },
      },
      min: 0,
      max: max => Math.ceil(max * 1.15),
      stepSize: calculateStepSize(sortedMice),
      forceNiceScale: false,
      decimalsInFloat: 0,
      labels: {
        formatter: value => Math.floor(value).toString(),
      },
    },
    colors: ['#6A37D4', '#0ab39c', '#ffe529'],
  };

  return chartOptions;
};

function calculateStepSize(chartData: number[]) {
  const max = Math.max(...chartData);
  if (max < 10) {
    return 1;
  }
  const stepSize = max / 5;
  return stepSize;
}
